import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sparkles, Settings2, Zap, ShieldCheck, AlertTriangle,
  CheckCircle2, ListTodo, Target, AlertCircle, Newspaper,
  BarChart3, Loader2, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mode descriptions
const MODE_INFO = {
  disabled: {
    label: 'Disabled',
    description: 'All suggestions require manual review and approval',
    icon: ShieldCheck,
    color: 'text-slate-400'
  },
  high_only: {
    label: 'High Confidence Only',
    description: 'Auto-apply only suggestions with 90%+ confidence',
    icon: CheckCircle2,
    color: 'text-green-400'
  },
  high_medium: {
    label: 'High + Medium',
    description: 'Auto-apply suggestions with 75%+ confidence',
    icon: Zap,
    color: 'text-yellow-400'
  },
  all: {
    label: 'All Suggestions',
    description: 'Auto-apply all suggestions (not recommended)',
    icon: AlertTriangle,
    color: 'text-red-400'
  }
};

// Suggestion type icons
const TYPE_ICONS = {
  todo: ListTodo,
  rock: Target,
  issue: AlertCircle,
  headline: Newspaper,
  scorecard: BarChart3
};

/**
 * Auto-Apply Settings Component for AI Copilot Phase C
 * Allows facilitators to configure automatic application of AI suggestions
 */
const AutoApplySettings = ({
  meetingId,
  open,
  onOpenChange,
  onSettingsUpdated
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [mode, setMode] = useState('disabled');
  const [confidenceThreshold, setConfidenceThreshold] = useState(90);
  const [enabledTypes, setEnabledTypes] = useState(['todo', 'headline']);
  const [requireReview, setRequireReview] = useState(true);
  const [notifyOnApply, setNotifyOnApply] = useState(true);
  const [maxPerSection, setMaxPerSection] = useState(3);
  
  // Load current settings
  useEffect(() => {
    if (open && meetingId) {
      loadSettings();
    }
  }, [open, meetingId]);
  
  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/eos/meetings/${meetingId}/auto-apply/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const settings = response.data.settings;
      setMode(settings.mode);
      setConfidenceThreshold(Math.round(settings.confidence_threshold * 100));
      setEnabledTypes(settings.enabled_types);
      setRequireReview(settings.require_facilitator_review);
      setNotifyOnApply(settings.notify_on_apply);
      setMaxPerSection(settings.max_auto_applies_per_section);
      
    } catch (error) {
      console.error('Error loading auto-apply settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/eos/meetings/${meetingId}/auto-apply/settings`,
        {
          mode,
          confidence_threshold: confidenceThreshold / 100,
          enabled_types: enabledTypes,
          require_facilitator_review: requireReview,
          notify_on_apply: notifyOnApply,
          max_auto_applies_per_section: maxPerSection
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Auto-apply settings saved');
      onSettingsUpdated?.({ mode, confidenceThreshold, enabledTypes });
      onOpenChange(false);
      
    } catch (error) {
      console.error('Error saving auto-apply settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const toggleType = (type) => {
    setEnabledTypes(prev => 
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };
  
  const ModeIcon = MODE_INFO[mode]?.icon || ShieldCheck;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-400" />
            Auto-Apply Settings
          </DialogTitle>
          <DialogDescription>
            Configure how AI suggestions are automatically applied during the meeting.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Mode Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Auto-Apply Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {Object.entries(MODE_INFO).map(([key, info]) => {
                    const Icon = info.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${info.color}`} />
                          <span>{info.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {MODE_INFO[mode]?.description}
              </p>
            </div>
            
            {/* Confidence Threshold */}
            {mode !== 'disabled' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Confidence Threshold</Label>
                  <Badge variant="outline" className={`
                    ${confidenceThreshold >= 90 ? 'border-green-500 text-green-400' : 
                      confidenceThreshold >= 75 ? 'border-yellow-500 text-yellow-400' : 
                      'border-red-500 text-red-400'}
                  `}>
                    {confidenceThreshold}%
                  </Badge>
                </div>
                <Slider
                  value={[confidenceThreshold]}
                  onValueChange={(v) => setConfidenceThreshold(v[0])}
                  min={50}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>50% (Low)</span>
                  <span>75% (Medium)</span>
                  <span>100% (High)</span>
                </div>
              </div>
            )}
            
            {/* Enabled Types */}
            {mode !== 'disabled' && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Auto-Apply Types</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(TYPE_ICONS).map(([type, Icon]) => (
                    <div
                      key={type}
                      className={`
                        flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors
                        ${enabledTypes.includes(type) 
                          ? 'bg-purple-500/20 border-purple-500' 
                          : 'bg-slate-800 border-slate-700 hover:border-slate-600'}
                      `}
                      onClick={() => toggleType(type)}
                    >
                      <Checkbox 
                        checked={enabledTypes.includes(type)}
                        onCheckedChange={() => toggleType(type)}
                      />
                      <Icon className="h-4 w-4" />
                      <span className="text-sm capitalize">{type}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Select which types of suggestions can be auto-applied
                </p>
              </div>
            )}
            
            {/* Additional Options */}
            {mode !== 'disabled' && (
              <div className="space-y-3 pt-2 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Notify on Apply</Label>
                    <p className="text-xs text-slate-500">Show notification when suggestions are applied</p>
                  </div>
                  <Switch
                    checked={notifyOnApply}
                    onCheckedChange={setNotifyOnApply}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Max Per Section</Label>
                    <p className="text-xs text-slate-500">Limit auto-applies per meeting section</p>
                  </div>
                  <Select value={maxPerSection.toString()} onValueChange={(v) => setMaxPerSection(parseInt(v))}>
                    <SelectTrigger className="w-20 bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {[1, 2, 3, 5, 10].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Warning for aggressive modes */}
            {mode === 'all' && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-red-400">Not Recommended</p>
                  <p className="text-red-300/80 text-xs mt-1">
                    Applying all suggestions may create unwanted items. Use with caution.
                  </p>
                </div>
              </div>
            )}
            
            {/* Info box */}
            {mode === 'disabled' && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-slate-800">
                <Info className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-400">
                  <p>All AI suggestions will appear in the Review & Apply panel for manual approval.</p>
                </div>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Settings2 className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoApplySettings;
